import { useState, useRef, useEffect } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mic, MicOff, Send, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { doNothing } from '@/utils/doNothing';
import { createRecording, notesQueryKey } from '@/api/recordings';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useVoiceRecord } from '@/hooks/voice-record/useVoiceRecord';
import { AnalyticsEventName } from '@/analytics/analytics.events';
import { track } from '@/analytics/track';

export interface CaptureDrawerProps {
  open: boolean;
  onClose: () => void;
  onCapture?: (text: string) => void;
}

const isQuestion = (input: string): boolean => {
  const trimmed = input.trim().toLowerCase();
  return (
    trimmed.endsWith('?') ||
    /^(what|when|where|who|why|how|did|do|does|is|are|was|were|can|could|have|has|any)\b/.test(trimmed)
  );
};

export const CaptureDrawer: FC<CaptureDrawerProps> = ({ open, onClose, onCapture }) => {
  const { t } = useTranslation('shared-components');
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [aiAnswer, setAiAnswer] = useState<string | null>(null);
  const [hasUsedVoice, setHasUsedVoice] = useState(false);
  const [lastFileId, setLastFileId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasAttemptedSubmitRef = useRef(false);
  const hasTrackedStartRef = useRef(false);
  const hasStartedDictationRef = useRef(false);
  const submissionModeRef = useRef<'typing' | 'dictation'>('typing');
  const dialogRef = useFocusTrap<HTMLDivElement>(open);

  const createRecordingMutation = useMutation({
    mutationFn: (inputText: string) =>
      createRecording({
        text: inputText,
        inputType: hasUsedVoice ? 'voice' : 'text',
        fileId: lastFileId ?? null,
      }),
    onSuccess: (data, submittedText) => {
      track(AnalyticsEventName.RECORDING_SUBMITTED, {
        submission_mode: submissionModeRef.current,
      });
      if (data.type === 'answer' || data.type === 'answer_and_processing') {
        setAiAnswer(data.answer);
        if (data.type === 'answer_and_processing') {
          queryClient.invalidateQueries({ queryKey: notesQueryKey }).catch(doNothing);
        }
      } else if (data.type === 'processing') {
        toast.success(t('capture-drawer.toast.logged'));
        queryClient.invalidateQueries({ queryKey: notesQueryKey }).catch(doNothing);
        onCapture?.(submittedText);
        setText('');
        onClose();
      } else {
        toast.error(t('capture-drawer.toast.error'));
      }
    },
    onError: (error) => {
      toast.error(error.message ?? t('capture-drawer.toast.error'));
    },
  });

  const isAsking = createRecordingMutation.isPending;

  const voiceRecord = useVoiceRecord({
    onTranscription: (transcribed, fileId) => {
      setText((prev) => (prev ? `${prev} ${transcribed}` : transcribed));
      setHasUsedVoice(true);
      if (fileId) {
        setLastFileId(fileId);
      }
    },
    onError: (msg) => toast.error(msg),
    onDictationAttempted: ({ permissionRequested, permissionGranted }) => {
      track(AnalyticsEventName.RECORDING_SWITCHED_TO_DICTATION, {
        permission_requested: permissionRequested,
        permission_granted: permissionGranted,
      });
    },
  });
  const voiceRecordRef = useRef(voiceRecord);
  voiceRecordRef.current = voiceRecord;

  useEffect(() => {
    if (open) {
      hasAttemptedSubmitRef.current = false;
      hasTrackedStartRef.current = false;
      hasStartedDictationRef.current = false;
      setTimeout(() => textareaRef.current?.focus(), 300);
    } else {
      voiceRecordRef.current.stopRecording(true);
      setText('');
      setAiAnswer(null);
      setHasUsedVoice(false);
      setLastFileId(null);
    }
  }, [open]);

  const trackRecordingStart = (initialMode: 'typing' | 'dictation') => {
    if (hasTrackedStartRef.current) {
      return;
    }
    hasTrackedStartRef.current = true;
    track(AnalyticsEventName.RECORDING_STARTED, { initial_mode: initialMode });
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    trackRecordingStart('typing');
    if (hasStartedDictationRef.current) {
      hasStartedDictationRef.current = false;
      track(AnalyticsEventName.RECORDING_SWITCHED_TO_TYPING, {});
    }
    setText(e.target.value);
    setAiAnswer(null);
  };

  const toggleVoice = () => {
    if (!voiceRecord.isRecording) {
      hasStartedDictationRef.current = true;
      trackRecordingStart('dictation');
    }
    voiceRecord.toggleRecording();
  };

  const handleSend = () => {
    if (!text.trim()) {
      return;
    }
    hasAttemptedSubmitRef.current = true;
    submissionModeRef.current = voiceRecord.isRecording || hasStartedDictationRef.current ? 'dictation' : 'typing';
    setAiAnswer(null);
    createRecordingMutation.mutate(text.trim());
  };

  const handleClose = () => {
    if (!hasAttemptedSubmitRef.current) {
      track(AnalyticsEventName.RECORDING_CANCELLED, {});
    }
    if (voiceRecord.isRecording) {
      voiceRecord.stopRecording(true);
    }
    onClose();
  };
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  const handleBackdropKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  // The drawer stays mounted so it can slide in and out, so it must be pulled
  // out of the tab order and the accessibility tree while it is closed.
  useEffect(() => {
    dialogRef.current?.toggleAttribute('inert', !open);
  }, [open, dialogRef]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm transition-opacity duration-300',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={handleClose}
        onKeyDown={handleBackdropKeyDown}
        role='presentation'
      />

      {/* Drawer */}
      <div
        ref={dialogRef}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out',
          open ? 'translate-y-0' : 'translate-y-full',
        )}
        role='dialog'
        aria-modal='true'
        aria-hidden={!open}
        aria-label={t('capture-drawer.aria.drawer')}
      >
        <div className='max-h-[85vh] overflow-y-auto rounded-t-3xl bg-card shadow-2xl'>
          {/* Handle */}
          <div className='flex justify-center pb-1 pt-3'>
            <div className='h-1 w-10 rounded-full bg-muted-foreground/20' />
          </div>

          <div className='mx-auto max-w-md px-5 pb-[calc(2rem+env(safe-area-inset-bottom))]'>
            {/* Header */}
            <div className='mb-4 flex items-center justify-between pt-2'>
              <p className='text-sm font-medium text-muted-foreground'>{t('capture-drawer.prompt')}</p>
              <button
                type='button'
                onClick={handleClose}
                className='flex h-8 w-8 items-center justify-center rounded-full bg-muted'
                aria-label={t('capture-drawer.aria.close')}
              >
                <X
                  className='h-3.5 w-3.5 text-muted-foreground'
                  aria-hidden
                />
              </button>
            </div>

            {/* Input area with mic */}
            <div className='mb-3 flex items-start gap-3'>
              <label
                htmlFor='capture-drawer-input'
                className='sr-only'
              >
                {t('capture-drawer.input-label')}
              </label>
              <textarea
                id='capture-drawer-input'
                ref={textareaRef}
                value={text}
                onChange={handleTextChange}
                placeholder={t('capture-drawer.input-placeholder')}
                rows={3}
                className='flex-1 resize-none rounded-xl bg-background p-3 text-sm leading-relaxed outline-none placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-primary/20'
              />
              <button
                type='button'
                onClick={toggleVoice}
                disabled={voiceRecord.isTranscribing}
                className={cn(
                  'mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all',
                  voiceRecord.isRecording && 'animate-pulse bg-destructive text-destructive-foreground',
                  voiceRecord.isTranscribing && !voiceRecord.isRecording && 'bg-muted text-muted-foreground',
                  !voiceRecord.isRecording && !voiceRecord.isTranscribing && 'bg-primary text-primary-foreground',
                )}
                aria-label={
                  voiceRecord.isRecording
                    ? t('capture-drawer.aria.mic-stop')
                    : voiceRecord.isTranscribing
                      ? t('capture-drawer.aria.mic-transcribing')
                      : t('capture-drawer.aria.mic-start')
                }
              >
                {voiceRecord.isRecording ? (
                  <MicOff
                    className='h-4.5 w-4.5'
                    aria-hidden
                  />
                ) : voiceRecord.isTranscribing ? (
                  <div
                    className='h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-muted-foreground'
                    aria-hidden
                  />
                ) : (
                  <Mic
                    className='h-4.5 w-4.5'
                    aria-hidden
                  />
                )}
              </button>
            </div>

            {voiceRecord.isRecording && (
              <p className='mb-3 ml-14 animate-pulse text-xs text-destructive'>{t('capture-drawer.listening')}</p>
            )}

            {/* AI Answer */}
            {isAsking && (
              <div className='mb-4 flex items-center gap-3 rounded-xl bg-background p-4'>
                <div
                  className='h-4 w-4 animate-spin rounded-full border-2 border-primary/40 border-t-primary'
                  aria-hidden
                />
                <p className='text-sm text-muted-foreground'>{t('capture-drawer.thinking')}</p>
              </div>
            )}

            {aiAnswer && (
              <div className='animate-fade-in mb-4 rounded-xl bg-background p-4'>
                <p className='text-sm leading-relaxed'>{aiAnswer}</p>
              </div>
            )}

            {/* Send button */}
            <button
              type='button'
              onClick={handleSend}
              disabled={!text.trim() || isAsking}
              className='flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-semibold text-primary-foreground transition-transform active:scale-[0.98] disabled:opacity-30'
            >
              {text.trim() && isQuestion(text.trim()) ? (
                <>
                  <Search
                    className='h-4 w-4'
                    aria-hidden
                  />{' '}
                  {t('capture-drawer.buttons.ask')}
                </>
              ) : (
                <>
                  <Send
                    className='h-4 w-4'
                    aria-hidden
                  />{' '}
                  {t('capture-drawer.buttons.done')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CaptureDrawer;
